import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImgStyled, Content } from './img.styled';
import { NoVideo } from '../no-video/no-video';

// If a stream <img> hasn't produced a frame (fired onLoad) within this window,
// stop spinning and show the polished "no video signal" placeholder instead of
// an endless spinner. A working MJPEG stream fires onLoad well within this, and
// if a frame arrives even after the timeout onLoad restores the live video.
const LOAD_TIMEOUT_MS = 8000;

// Below this rendered height (e.g. the small multi-cam selector thumbnails) the
// placeholder shows a compact icon-only variant so text never overflows.
const COMPACT_MAX_H = 120;

export const Img = (props) => {
  const { t } = useTranslation();
  const { src, title, afterError, realVideo = false, ...rest } = props;
  const [alt] = useState(title);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [compact, setCompact] = useState(false);
  const [vertical, setVertical] = useState(false);
  const imgRef = useRef(null);
  const contentRef = useRef(null);
  const timerRef = useRef(null);

  // (re)start the grace timer whenever the source changes
  useEffect(() => {
    setLoading(true);
    setError(false);
    setTimedOut(false);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setTimedOut(true), LOAD_TIMEOUT_MS);
    return () => clearTimeout(timerRef.current);
  }, [src]);

  // adapt the placeholder to the panel size (full vs. thumbnail)
  useEffect(() => {
    const h = contentRef.current?.clientHeight ?? 0;
    if (h > 0) {
      setCompact(h < COMPACT_MAX_H);
    }
  });

  const onLoad = () => {
    clearTimeout(timerRef.current);
    setError(false);
    setTimedOut(false);
    setLoading(false);
    const w = imgRef.current.naturalWidth;
    const h = imgRef.current.naturalHeight;
    // console.log("w", w, h);
    setVertical(w < h);
  };

  const onError = () => {
    clearTimeout(timerRef.current);
    setLoading(false);
    setError(true);
    if (afterError) {
      setTimeout(() => {
        setError(false);
        afterError();
      }, 10000);
    }
  };

  const noSignal = error || timedOut;
  // keep the spinner only while genuinely connecting; never together with the placeholder

  const cl = `img-block ${noSignal ? ' no-signal' : loading ? ' loading' : error ? ' error' : ''} ${vertical ? ' vertical' : ''} ${realVideo ? ' real-video' : ''}`;

  return (
    <Content ref={contentRef} className={cl}>
      <ImgStyled src={src} alt={alt} title={alt} onLoad={onLoad} onError={onError} {...rest} ref={imgRef} />
      {noSignal && (
        <NoVideo size={compact ? 24 : 46} title={compact ? '' : t('dock.Нет видеосигнала')} sub={compact ? '' : t('dock.Камера недоступна')} />
      )}
    </Content>
  );
};
